import i18n from "../i18n";
import { IBasicStats, IPopulationDetailsStats, ITaxStats, KatoType } from "../types/kato.types";
import { ILandFundItem } from "../types/screening.types";
import { IFile, InfrastructureProjectType } from "../types/project.types";

export const getKatoType = (kato: string | number): KatoType => {
    const str = kato.toString().padEnd(10, '0');
    if (/^.{2}00000000$/.test(str)) return KatoType.OBLAST;
    if (/^.{4}000000$/.test(str)) return KatoType.REGION;
    if (/^.{6}0000$/.test(str)) return KatoType.DISTRICT;
    if (/^.{8}00$/.test(str)) return KatoType.VILLAGE;
    throw new Error(`Invalid KATO code: ${kato}`);
}

// export const t = (key: string, lng: string = 'ru', options?: any) =>
//     i18n.getResource(lng.toLowerCase(), 'translation', key, options)
//     || i18n.getResource((lng === 'ru' ? 'kz' : lng).toLowerCase(), 'translation', key, options)
//     || key;

export const t = (key: string, lng: string = 'ru', options: Record<string, string | number> = {}) => {
    const lang = lng.toLowerCase();
    const fallbackLang = lang === 'ru' ? 'kz' : lang;

    const template =
        i18n.getResource(lang, 'translation', key) ||
        i18n.getResource(fallbackLang, 'translation', key) ||
        key;

    if (template === key) {
        return i18n.getResource('ru', 'translation', key) || key;
    }

    return template.replace(/{{(\w+)}}|{(\w+)}/g, (_: any, m1: any, m2: any) => {
        const varName = m1 || m2;
        return options[varName] != null ? String(options[varName]) : '';
    });
};

export const getLang = () => {
    return i18n.language.includes('kz') ? 'Kz' : 'Ru';
}

export const getToFixed = (value: number, toFixed: number = 2): string => {
    if (Number.isNaN(value) || !Number.isFinite(value)) return value.toString();
    return Number.isInteger(value) ? value.toString() : value.toFixed(toFixed);
}

export const getPopulationDetails = (stats: IPopulationDetailsStats, key: string) => {
    const femaleCount = stats.femaleCount ? +stats.femaleCount : 0;
    const maleCount = stats.maleCount ? +stats.maleCount : 0;

    const immigrationCount = stats.immigrationCount ? +stats.immigrationCount : 0;
    const emigrationCount = stats.emigrationCount ? +stats.emigrationCount : 0;

    const populationEmploymentCount = stats.populationEmploymentCount ? +stats.populationEmploymentCount : 0;

    const birthCount = stats.birthCount ? +stats.birthCount : 0;
    const deathCount = stats.deathCount ? +stats.deathCount : 0;

    if (key === 'populationCount') {
        return femaleCount + maleCount;
    } else if (key === 'saldoMigration') {
        return immigrationCount - emigrationCount;
    } else if (key === 'economicallyActivePopulation') {
        return femaleCount === 0 || maleCount === 0 || populationEmploymentCount === 0 ? 0 : ((populationEmploymentCount / (femaleCount + maleCount)) * 100).toFixed(1);
    } else if (key === 'naturalCount') {
        return birthCount - deathCount;
    }

    return 0;
}

export const onlyLetters = (value: string) => value.replace(/[^\p{L}\s]/gu, '');

export const keepCapitalized = (str: string) => str?.split(' ').map(str => str.charAt(0) + str.slice(1).toLowerCase()).join(' ').replace(/  +/g, ' ');

export const getIncreaseVal = (stat: IBasicStats, basicStats: IBasicStats[]) => {
    const item = basicStats?.find((item: any) => item.type === stat.type && item.subType === stat.subType);

    if (!item) return 0;
    const current = item?.currentYearVal ? +item?.currentYearVal : 0;
    const previous = item?.previousYearVal ? +item?.previousYearVal : 0;
    if (current === 0 || previous === 0) return 0;

    const res = ['SER', 'MCB', 'EMPLOYMENT', 'LIVESTOCK'].includes(item.type) ? (((current - previous) / previous) * 100).toFixed(1) : item.type === 'BUDGET' ? ((current / previous) * 100).toFixed(2) : (current - previous).toFixed(2);
    return res.endsWith('.00') || res.endsWith('.0')
        ? res.split('.')[0]
        : res;
}

export const isEmpty = (value: any): boolean => value === null || value === '' || value === undefined;

export const getTaxValue = (data: ITaxStats, key: keyof ITaxStats): number => {
    const taxRevenuesFact = data.taxRevenuesFact ? +data.taxRevenuesFact : 0;
    const nonTaxRevenuesFact = data.nonTaxRevenuesFact ? +data.nonTaxRevenuesFact : 0;
    const fixedAssetsSaleFact = data.fixedAssetsSaleFact ? +data.fixedAssetsSaleFact : 0;

    const taxRevenuesPlan = data.taxRevenuesPlan ? +data.taxRevenuesPlan : 0;
    const nonTaxRevenuesPlan = data.nonTaxRevenuesPlan ? +data.nonTaxRevenuesPlan : 0;
    const fixedAssetsSalePlan = data.fixedAssetsSalePlan ? +data.fixedAssetsSalePlan : 0;

    const taxRevenuesAnnual = data.taxRevenuesAnnual ? +data.taxRevenuesAnnual : 0;
    const nonTaxRevenuesAnnual = data.nonTaxRevenuesAnnual ? +data.nonTaxRevenuesAnnual : 0;
    const fixedAssetsSaleAnnual = data.fixedAssetsSaleAnnual ? +data.fixedAssetsSaleAnnual : 0;

    if (key === 'fact') {
        return +(taxRevenuesFact + nonTaxRevenuesFact + fixedAssetsSaleFact).toFixed(1);
    } else if (key === 'plan') {
        return +(taxRevenuesPlan + nonTaxRevenuesPlan + fixedAssetsSalePlan).toFixed(1);
    } else if (key === 'annual') {
        return +(taxRevenuesAnnual + nonTaxRevenuesAnnual + fixedAssetsSaleAnnual).toFixed(1);
    } else if (key === 'planPercent') {
        return +getTaxValue(data, 'fact') === 0 || +getTaxValue(data, 'plan') === 0 ? 0 : +((+getTaxValue(data, 'fact') / +getTaxValue(data, 'plan')) * 100).toFixed(1);
    } else if (key === 'annualPercent') {
        return +getTaxValue(data, 'fact') === 0 || +getTaxValue(data, 'annual') === 0 ? 0 : +((+getTaxValue(data, 'fact') / +getTaxValue(data, 'annual')) * 100).toFixed(1);
    } else if (key === 'taxRevenuesPlanPercent') {
        return taxRevenuesFact === 0 || taxRevenuesPlan === 0 ? 0 : +((taxRevenuesFact / taxRevenuesPlan) * 100).toFixed(1);
    } else if (key === 'nonTaxRevenuesPlanPercent') {
        return nonTaxRevenuesFact === 0 || nonTaxRevenuesPlan === 0 ? 0 : +((nonTaxRevenuesFact / nonTaxRevenuesPlan) * 100).toFixed(1);
    } else if (key === 'fixedAssetsSalePlanPercent') {
        return fixedAssetsSaleFact === 0 || fixedAssetsSalePlan === 0 ? 0 : +((fixedAssetsSaleFact / fixedAssetsSalePlan) * 100).toFixed(1);
    } else if (key === 'taxRevenuesAnnualPercent') {
        return taxRevenuesFact === 0 || taxRevenuesAnnual === 0 ? 0 : +((taxRevenuesFact / taxRevenuesAnnual) * 100).toFixed(1);
    } else if (key === 'nonTaxRevenuesAnnualPercent') {
        return nonTaxRevenuesFact === 0 || nonTaxRevenuesAnnual === 0 ? 0 : +((nonTaxRevenuesFact / nonTaxRevenuesAnnual) * 100).toFixed(1);
    } else if (key === 'fixedAssetsSaleAnnualPercent') {
        return fixedAssetsSaleFact === 0 || fixedAssetsSaleAnnual === 0 ? 0 : +((fixedAssetsSaleFact / fixedAssetsSaleAnnual) * 100).toFixed(1);
    }

    return 0;
}

export const countTotal = (type: 'reserve' | 'agricultural', data: ILandFundItem) => {
    return +data[`${type}_arable`] + +data[`${type}_pasture`];
}

export const getRegionTotals = (items: any, key: string): string => {
    const list = items;
    if (list.length > 0) {
        if (key.toLowerCase().includes('percentage')) {
            return ((+getRegionTotals(list, key.replace('Percentage', 'Km')) / +getRegionTotals(list, 'totalKm')) * 100).toFixed(2);
        } else {
            return list.reduce((acc: number, item: any) => acc + +getObjectTotals(item.roadsList, key), 0).toFixed(2);
        }
    }
    return ''
}

export const getObjectTotals = (items: any, key: string): string => {
    const list = items;
    if (list.length > 0) {
        if (key.toLowerCase().includes('percentage')) {
            return ((+getObjectTotals(list, key.replace('Percentage', 'Km')) / +getObjectTotals(list, 'totalKm')) * 100).toFixed(2);
        } else if (key.toLowerCase().includes('fine')) {
            if (key.toLowerCase().includes('prev')) {
                return (+getObjectTotals(list, 'prevYearGoodConditionKm') + +getObjectTotals(list, 'prevYearSatisfactoryConditionKm')).toFixed(2);
            } else {
                return (+getObjectTotals(list, 'goodConditionKm') + +getObjectTotals(list, 'satisfactoryConditionKm')).toFixed(2);
            }
        }
        else {
            return list.reduce((acc: number, item: any) => acc + +item[key], 0).toFixed(2);
        }
    }
    return '';
};

export const calculateRoadSharePercentage = (val: number, total: number) => {
    const totalVal = total ? total : 0;
    const curr = val ? +val : 0;
    return totalVal === 0 ? 0 : ((curr / totalVal) * 100).toFixed(1);
};

export const findOptionByValue = (value: string | number | null, options: { label: string; value: string }[]) => {
    if (value === null || value === undefined) return null;
    return options.find(option => option.value.toString() === value.toString()) || null;
}

export const convertFilesToImageDate = (files: IFile[]) => {
    return files.reduce((acc, file) => {
        const date = file.date ? new Date(file.date).toISOString().split('T')[0] : '';
        if (file.fileType === 'IMAGE_AFTER') {
            acc.IMAGE_AFTER.push({ id: file.id, date });
        } else if (file.fileType === 'IMAGE_CURRENT') {
            acc.IMAGE_CURRENT.push({ id: file.id, date });
        }
        return acc;
    }, { IMAGE_AFTER: [] as { id: number; date: string }[], IMAGE_CURRENT: [] as { id: number; date: string }[] });
}

export const toTranslationKey = (key: string): string => {
    return key?.toLowerCase().replaceAll('_', '-')
}

export const isInfrastructureProjectType = (val: string): val is InfrastructureProjectType => {
  return Object.values(InfrastructureProjectType).includes(val as InfrastructureProjectType);
};
